import { useCallback } from 'react';

import { deriveBasename } from '@jane/bloom/utils/deriveBasename';

import { useRootLoaderData } from './useRootLoaderData';

/**
 * Provides a function that performs a hard navigation (full page load) to a
 * relative path.
 *
 * This type of navigation is necessary around authentication during checkout, to
 * ensure the cart reloads with the newly logged-in user. It also helps establish
 * consistency for auth redirects even outside of checkout.
 *
 * This is the single place where the basename is re-attached for navigations
 * that bypass React Router (`window.location`). Unlike `redirect()` and
 * `navigate()` — which the router prefixes with its configured basename — a
 * `window.location` assignment goes straight to the browser, so the basename
 * must already be present in the URL.
 */
export const useHardRedirect = () => {
  const { menuUrl } = useRootLoaderData();

  return useCallback(
    (relativePath: string) => {
      const basename = deriveBasename(menuUrl);
      window.location.href = `${basename}${relativePath}`;
    },
    [menuUrl]
  );
};
